/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { NavLink } from '@/components/link';
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    NavigationMenuTrigger,
    NavigationMenuViewport,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { useBrand } from '@/lib/brand/use-brand';
import { NAV_SALE_EMPHASIS, navSaleEmphasisAttr } from '@/lib/brand/nav-sale-emphasis';
import type { NavSilo } from '@/lib/adapters/nav-content';
import { useNavContext } from './context';
import CuratedFlyoutPanel, { targetHref } from './flyout';

// eslint-disable-next-line react-refresh/only-export-components -- useNavContext is the co-located hook of this provider family
export { NavContentProvider, useNavContext } from './context';
export { default as CuratedMobileNav } from './mobile';

/*
 * Per-brand layout switch — same rationale as navigation-menu-mega's
 * NAV_SWITCH: NM switches at 768px (`md`); BG keeps the hamburger through 1024
 * and takes over at 1025 (BG_DESKTOP_MIN_WIDTH_PX in lib/brand/header-
 * composition.ts carries the captures).
 *
 * This arm read `lg` until 2026-08-20, which fires AT 1024 — one pixel early,
 * on the last width BG still serves mobile. THIS is the mounted nav (routes/
 * _app.tsx renders CuratedDesktopNav/CuratedMobileNav; navigation-menu-mega is
 * reached only from stories), so this arm is the one that would have bitten.
 * It did not: measured at 1023/1024/1025/1026, an ancestor gate held the row at
 * 0x0 through 1024 regardless of this class. That masking is why the defect was
 * invisible, and why it is fixed rather than left as "works today".
 *
 * Complete static strings, never computed class names — Tailwind's scanner
 * reads source text and emits nothing for a class built by template. The active
 * breakpoint is exposed as data-nav-breakpoint so tests assert the state
 * attribute, which is also why that attribute must not be allowed to go stale:
 * it was reporting `lg` on a header that had switched at 1025 since e792a60.
 */
const NAV_SWITCH = {
    nm: { breakpoint: 'md', mobileOnly: 'md:hidden', desktopOnly: 'hidden md:flex' },
    bg: {
        breakpoint: 'bgnav',
        mobileOnly: 'bgnav:hidden',
        desktopOnly: 'hidden bgnav:flex',
    },
} as const;

/**
 * Trigger typography: `labelMediumDefault` — the same role the previous
 * catalog nav applied (see the phase-2 note in navigation-menu-mega).
 *
 * Constant spacing at every width (.reference/harvest/micro-harvest-header2.md
 * §5): live's silo row applies NO horizontal compression — 13px Futura with
 * normal tracking and a constant 20px column-gap from 1440 all the way down,
 * the row overflowing the viewport once it no longer fits (see the nav-row
 * comment in components/header/index.tsx). The former px-1/lg:px-2/2xl:px-3
 * compression ladder is retired for one constant padding value.
 *
 * Link text (owner-confirmed blast-1 values, 2026-08-16): 18px / letter-spacing
 * 0.5px / regular weight via the --header-nav-link-* tokens composed over the
 * labelMedium role (its line-height survives). NO text-transform — Title Case
 * comes from the data labels as-authored. 18px is deliberately off the luxe
 * role scale (§2 jumps 16 → 20) — ledgered as a one-off element value. BG pins
 * its previous role rendering in the brand-bg.css parity guard.
 */
// The hover:* / focus:* / data-[state=open]:* utilities strip shadcn's
// NavigationMenuTrigger chrome — a grey fill plus a text-colour swap on hover.
// Framework chrome is never design (rule 8d). Focus-visible rings are untouched
// (a11y).
//
// CORRECTED 2026-08-23 — this note used to end "Live NM has NO hover rule of any
// kind on these links (verified: zero matching :hover declarations); the flyout
// opening IS the feedback." The parenthesis is still true and the sentence after
// it was WRONG, and the two were never about the same thing. That check searched
// for a :hover DECLARATION. Live's feedback is on the item whose flyout is OPEN,
// which is a different state carried by a different mechanism, so no :hover grep
// could ever have seen it — searching for the mechanism you expect to be missing
// instead of the one the site uses is a failure this project has paid for before.
//
// LIVE'S OPEN STATE IS A WEIGHT PLUS AN UNDERLINE. Measured on the owner's own
// 1728 captures of 2026-08-23, reproducing on 5 of 5 with a flyout open: exactly
// one of the thirteen silos carries ~40% more ink per px than its twelve
// siblings, is ~5% wider than the same word when closed, and its stems go from 2
// device px to 3-4 — a bold. Under it sits a 5.0 CSS solid rgb(0,0,0) rule
// spanning the text run rather than the padded box. The ink COLOUR does not
// change: every silo reads rgb(46,46,46) open or closed.
//
// IT IS NOT IMPLEMENTED IN THIS FILE. The rule lives in
// theme/overrides/navigation.css keyed on [data-state='open'], reading
// --flyout-nav-open-* from tokens/brand-nm/header.css, so the values stay
// brand-scoped and this className stays free of a state it does not own.
//
// ALSO STALE BELOW, and left in place rather than silently deleted: the
// "⚠ PLUMBED BUT NOT PAINTING" block. That blocker was fixed — the one line it
// asks for is now at navigation.css:27-29 — and the resting trigger measures
// rgb(46,46,46) on the running app at 1728. What is still true is the divergence
// the block does not mention: on hover our trigger swaps to rgb(0,0,0) where
// live holds 46,46,46.
// COLOUR JOINS THE --header-nav-link-* FAMILY. Until now these links carried no
// colour of their own and inherited the header's `text-header-foreground`
// (rgb(0,0,0) on both brands, measured). Live NM paints its silo row
// rgb(46,46,46) — see the derivation and the five widths beside
// `--header-nav-link-color` in theme/tokens/brand-nm/header.css.
//
// WRITTEN AS `var(--token, <neutral>)` RATHER THAN A SHARED DECLARATION. Only
// NM has a measured value here; BG's live silo-link reads rgb(0,0,0)
// (.reference/bg-homepage.desktop.styles.json -> navLink, _tag=A
// _cls=silo-link clickable-silo), which is what it inherits today. Declaring a
// neutral in the shared tokens/header.css would make that a shared decision
// nobody took; the fallback keeps the property undefined for BG, so an unset
// brand re-asserts the inherited colour and this is inert by construction.
// Proven inert rather than reasoned: BG's 13 links measured rgb(0,0,0) at 390,
// 1024 and 1280 before and after, every box unchanged.
//
// ⚠ PLUMBED BUT NOT PAINTING. This class is served and matches, the token
// resolves rgb(46,46,46) on this element, and the links still compute
// rgb(0,0,0) — because `theme/overrides/navigation.css:14-16` is UNLAYERED:
//
//   header [data-slot='navigation-menu-trigger'] { color: var(--header-foreground); }
//
// and unlayered beats @layer utilities regardless of specificity. Read off the
// element at 1280 with CDP getMatchedStylesForNode on a settled page, no
// mutation: this utility is cascade order 18 in layer `utilities`, that rule is
// order 38 with no layer, and 38 wins. That stylesheet's own comment already
// records the same defeat for `.text-brand-sale-red`, measured 2026-08-20.
//
// THE ONE LINE THAT LANDS IT is in that file, not this one — give the blanket
// rule the same fallback this class uses:
//
//   header [data-slot='navigation-menu-trigger'] {
//       color: var(--header-nav-link-color, var(--header-foreground));
//   }
//
// It is inert for BG and for any unset brand by construction: the fallback is
// the value that rule already delivers. This class then becomes redundant
// rather than wrong, and can go with it.
//
// WHY THE CLASS AND THE TOKEN STAY MEANWHILE. The value is measured at five
// widths (see brand-nm/header.css) and the alternative is deleting it back into
// prose, which is how it stayed unapplied for the last two weeks. The blocker
// is written into the code that carries it, which is the same convention that
// made brand-bg.css's "NOT LANDED, and deliberately" note findable.
//
// THE HOVER ARM IS NOT DECORATION. `hover:text-inherit` / `focus:text-inherit`
// strip shadcn's `hover:text-accent-foreground` (tailwind-merge drops it — same
// group). `inherit` resolves up to `text-header-foreground` = rgb(0,0,0), so
// once the override line above lands, a base of 46 with an inherited hover would
// jump the row BLACK on hover — which live does not do (live NM has no hover
// rule on these links at all). The arbitrary property after the pair is what
// holds the measured value through both states. UNVERIFIED IN THE HOVER STATE:
// the resting state does not paint yet, so there is nothing to hover-test until
// the override line lands, and the control carries `transition-[color]`, which
// makes a naive getComputedStyle read after a synthetic hover return a value in
// flight rather than the settled one.
// SPELLED OUT IN FULL, NEVER COMPOSED. Tailwind finds candidates by scanning
// SOURCE TEXT, so a class assembled from a template literal or a shared const
// is invisible to it and no rule is emitted — the element carries the class, the
// token resolves on it, and nothing paints. That failure is silent from every
// angle a reviewer normally checks (class present in the DOM, custom property
// computing the right value, no cascade conflict) and it is only visible by
// grepping the SERVED stylesheet: this exact colour was written as
// `[color:${NAV_LINK_COLOR}]` first and produced ONE occurrence of
// `header-nav-link-color` across 417KB of served CSS — its own declaration in
// brand-nm/header.css — against 5 for the `--header-nav-link-size` beside it.
// Repeating the long var() four times is the cost of being scannable.
const TRIGGER_CLASSES =
    'type-label-medium text-(length:--header-nav-link-size) tracking-(--header-nav-link-tracking) [text-transform:var(--header-nav-link-transform)] [color:var(--header-nav-link-color,var(--header-foreground))] px-(--header-nav-link-padding-x) hover:bg-transparent hover:text-inherit hover:[color:var(--header-nav-link-color,var(--header-foreground))] focus:bg-transparent focus:text-inherit focus:[color:var(--header-nav-link-color,var(--header-foreground))] data-[state=open]:bg-transparent data-[state=open]:hover:bg-transparent';

/**
 * Desktop curated navigation — the merchandised silo row plus full-width
 * flyout panels, fed by the nav-content adapter through NavContentProvider.
 *
 * Trigger semantics (live-NM parity — COMPARISON.md "Trigger element"): each
 * top-level silo is a real ANCHOR that navigates on click/Enter, while the
 * flyout opens on hover (Radix trigger behavior) and on keyboard FOCUS (the
 * `onFocus` below, so keyboard users reach panel content without giving up
 * Enter-to-navigate). The menu value is controlled so focus can open it and
 * route changes can close it; Radix keeps `aria-expanded` on the anchor
 * correct throughout.
 */
export function CuratedDesktopNav() {
    const nav = useNavContext();
    const navSwitch = NAV_SWITCH[useBrand()];
    const [openSilo, setOpenSilo] = useState('');
    const location = useLocation();

    // Close the panel after any navigation — an anchor-trigger click both
    // toggles the menu (Radix) and navigates (anchor default); without this
    // the panel would linger over the new route.
    useEffect(() => {
        setOpenSilo('');
    }, [location.pathname, location.search]);

    // Close the panel when the window or tab loses focus.
    //
    // This menu is CONTROLLED: `openSilo` is our React state, so Radix's own
    // dismiss paths cannot clear it on their own. Radix closes on pointerleave,
    // and switching tabs FROM THE KEYBOARD never moves the pointer, so that
    // event never fires. The panel is therefore still open on return, with the
    // cursor nowhere near the trigger whose leave would have closed it, and no
    // amount of mousing around the page will dismiss it. The taller the panel,
    // the harder it is to escape - which is why an unstyled, over-tall flyout
    // makes this feel much worse without being its cause.
    //
    // Native menus dismiss when their window loses focus. Match that.
    useEffect(() => {
        const close = () => setOpenSilo('');
        const onVisibility = () => {
            if (document.hidden) {
                close();
            }
        };
        window.addEventListener('blur', close);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('blur', close);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);

    if (!nav) {
        return null;
    }

    return (
        <div data-nav-breakpoint={navSwitch.breakpoint} className={cn(navSwitch.desktopOnly, 'items-center h-full')}>
            <NavigationMenu
                value={openSilo}
                onValueChange={setOpenSilo}
                delayDuration={0}
                viewport={false}
                data-viewport="true"
                // `static` kills the shadcn root's `relative` so the absolute
                // flyout below anchors to the HEADER (its nearest positioned
                // ancestor), not to this centered nav box.
                className="static"
                data-testid="curated-nav-desktop">
                {/* flex-nowrap + whitespace-nowrap: the silo row NEVER breaks to
                    a second line, and there is no scroll container.

                    It does not need one. Live fits all 13 silos at every width
                    it was captured at, and so do we now that the type and the
                    horizontal rhythm are live's (--header-nav-link-size /
                    -padding-x / -gap, brand-nm/header.css). An earlier version
                    of this comment claimed the row spilling past the viewport
                    edges was "as live does" — that was WRONG, and it is what let
                    an 18px row overflow 768 by 149px and scroll the whole page
                    sideways. If this row ever spills again, the type has drifted
                    off the captures; do not add a scroll container to hide it. */}
                <NavigationMenuList className="flex-nowrap whitespace-nowrap gap-(--header-nav-link-gap)">
                    {nav.activeContext.silos.map((silo) => (
                        <CuratedSiloItem key={silo.id} silo={silo} onOpen={setOpenSilo} />
                    ))}
                </NavigationMenuList>
                {/* Anchoring (owner, 2026-08-16: the flyout always renders BELOW
                    the cat nav, never covering it): the shadcn wrapper div
                    (`absolute top-full left-0`) is the positioned box — with
                    the nav root `static` and the header `relative`, it anchors
                    to the HEADER's bottom edge and scrolls with the document
                    (the old fixed/--header-height inner anchor drifted over
                    the nav on scroll). The wrapper is stretched full-header
                    (right:0) by a rule in overrides/navigation.css; the inner
                    viewport stays in flow and takes `md:w-full` so it fills
                    the wrapper instead of Radix's measured-content width var.
                    Do NOT reposition the INNER viewport (fixed/absolute):
                    out-of-flow makes the wrapper shrink-wrap to 0x0 and the
                    panel renders invisible — that regression shipped once. */}
                {/* Entrance = FADE ONLY (owner, 2026-08-16: live has a fade-in;
                    the old 8px menuSlideDown + shadcn's zoom-in read as a
                    weird settle-shift — no translate/zoom, opacity only). */}
                <NavigationMenuViewport className="border-0 shadow-lg md:w-full data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100 data-[state=open]:fade-in-0 data-[state=open]:duration-200 data-[state=closed]:animate-none" />
            </NavigationMenu>
        </div>
    );
}

function CuratedSiloItem({ silo, onOpen }: { silo: NavSilo; onOpen: (value: string) => void }) {
    const flyout =
        silo.flyout && (silo.flyout.columns?.length || silo.flyout.letterIndex || silo.flyout.tiles?.length)
            ? silo.flyout
            : undefined;
    // NOT a `text-brand-sale-red` utility: the unlayered
    // `header [data-slot='navigation-menu-trigger']` rule in
    // theme/overrides/navigation.css beats @layer utilities regardless of
    // specificity, so a class here computes to the header foreground and the
    // red never lands (measured 2026-08-20). The attribute below is the hook
    // that same unlayered block keys on. Absent unless THIS brand emphasises
    // on THIS surface — BG does here, NM does not.
    const linkClasses = TRIGGER_CLASSES;
    const saleEmphasis = navSaleEmphasisAttr(NAV_SALE_EMPHASIS[useBrand()].categoryNav, silo.emphasis);

    if (!flyout) {
        return (
            <NavigationMenuItem>
                <NavigationMenuLink asChild className={linkClasses}>
                    <NavLink
                        to={targetHref(silo.target)}
                        data-testid={`nav-silo-${silo.id}`}
                        data-emphasis={silo.emphasis}
                        data-sale-emphasis={saleEmphasis}
                        data-title={silo.label}>
                        {silo.label}
                    </NavLink>
                </NavigationMenuLink>
            </NavigationMenuItem>
        );
    }

    return (
        <NavigationMenuItem value={silo.id}>
            <NavigationMenuTrigger asChild data-has-submenu="true" className={linkClasses}>
                <NavLink
                    to={targetHref(silo.target)}
                    data-testid={`nav-silo-${silo.id}`}
                    data-emphasis={silo.emphasis}
                    data-sale-emphasis={saleEmphasis}
                    data-title={silo.label}
                    onFocus={() => onOpen(silo.id)}>
                    {silo.label}
                </NavLink>
            </NavigationMenuTrigger>
            <NavigationMenuContent className="!p-0 !left-auto !right-auto !w-full md:!w-full !animate-none !transition-none">
                {/* Live's panel frame (owner capture, 1280): 70px inline / 40px
                    block padding — NOT the page's section-container gutters. */}
                <div className="px-(--flyout-panel-pad-inline) py-(--flyout-panel-pad-block)">
                    <CuratedFlyoutPanel flyout={flyout} />
                </div>
            </NavigationMenuContent>
        </NavigationMenuItem>
    );
}
